// Admin templates editor — /admin/integration-templates
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  FormControlLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  listIntegrationTemplates,
  putIntegrationTemplate,
  deleteIntegrationTemplate,
  scanCustomerIntegrationsApi,
  type IntegrationTemplate,
  type ScanSummary,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, StatusPill, EmptyState, Icon } from "../../mui";

const c = brainTokens.color;

export function IntegrationTemplatesPageMui() {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<IntegrationTemplate[] | null>(null);
  const [selected, setSelected] = useState<IntegrationTemplate | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testCustomer, setTestCustomer] = useState("");
  const [testResult, setTestResult] = useState<ScanSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await listIntegrationTemplates(false));
    } catch {
      enqueueSnackbar("Failed to load templates.", { variant: "error" });
      setItems([]);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (t: IntegrationTemplate) => {
    setBusy(true);
    try {
      const saved = await putIntegrationTemplate(t.integrationKey, {
        name: t.name,
        description: t.description,
        defaultMigrationImpact: t.defaultMigrationImpact,
        resources: t.resources,
        enabled: t.enabled,
      });
      enqueueSnackbar("Saved.", { variant: "success" });
      setSelected(saved);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const runDryTest = async () => {
    if (!selected || !testCustomer.trim()) return;
    setBusy(true);
    try {
      const summary = await scanCustomerIntegrationsApi({
        customerId: testCustomer.trim(),
        dryRun: true,
      });
      setTestResult(summary);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Dry-run failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader
        title="Integration templates"
        eyebrow="Detection catalog for the scanner — enable patterns, then dry-run against a customer."
      />

      {!items ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState title="No templates — run the SQL seed migration." />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: selected ? "280px 1fr" : "1fr", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {items.map((t) => (
              <Box
                key={t.integrationKey}
                onClick={() => setSelected(t)}
                sx={{
                  p: "12px 14px",
                  border: `1px solid ${selected?.integrationKey === t.integrationKey ? c.teal : c.border}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  bgcolor: "#fff",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ fontWeight: 600, fontSize: 14 }}>{t.name}</Box>
                  <StatusPill tone={t.enabled ? "success" : "neutral"} label={t.enabled ? "on" : "off"} />
                </Box>
                <Box sx={{ fontSize: 11.5, color: c.muted2, fontFamily: brainTokens.font.mono }}>{t.integrationKey}</Box>
                <Box sx={{ fontSize: 12, color: c.muted2, mt: 0.5 }}>{t.resources.length} resource(s)</Box>
              </Box>
            ))}
          </Box>

          {selected && (
            <SectionCard title={selected.integrationKey}>
              <TextField
                label="Name"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                value={selected.name}
                onChange={(e) => setSelected({ ...selected, name: e.target.value })}
              />
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 1.5 }}
                value={selected.description ?? ""}
                onChange={(e) => setSelected({ ...selected, description: e.target.value })}
              />
              <TextField
                label="Default migration impact"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 1.5 }}
                value={selected.defaultMigrationImpact ?? ""}
                onChange={(e) => setSelected({ ...selected, defaultMigrationImpact: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={selected.enabled}
                    onChange={(e) => setSelected({ ...selected, enabled: e.target.checked })}
                  />
                }
                label="Enabled for scanner"
                sx={{ mb: 2 }}
              />

              <Box sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Resources</Box>
              {selected.resources.map((r, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "120px 1fr 80px auto", gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    label="type"
                    value={r.type}
                    onChange={(e) => {
                      const resources = [...selected.resources];
                      resources[i] = { ...r, type: e.target.value };
                      setSelected({ ...selected, resources });
                    }}
                  />
                  <TextField
                    size="small"
                    label="name_pattern"
                    value={r.name_pattern ?? ""}
                    onChange={(e) => {
                      const resources = [...selected.resources];
                      resources[i] = { ...r, name_pattern: e.target.value };
                      setSelected({ ...selected, resources });
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={r.check?.enabled !== false}
                        onChange={(e) => {
                          const resources = [...selected.resources];
                          resources[i] = { ...r, check: { enabled: e.target.checked } };
                          setSelected({ ...selected, resources });
                        }}
                      />
                    }
                    label="check"
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      setSelected({
                        ...selected,
                        resources: selected.resources.filter((_, j) => j !== i),
                      })
                    }
                  >
                    ✕
                  </Button>
                </Box>
              ))}
              <Button
                size="small"
                sx={{ mb: 2 }}
                onClick={() =>
                  setSelected({
                    ...selected,
                    resources: [
                      ...selected.resources,
                      { type: "lambda", name_pattern: "", check: { enabled: true } },
                    ],
                  })
                }
              >
                Add resource
              </Button>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" disabled={busy} onClick={() => void save(selected)}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Icon name="search" size={14} stroke={1.8} />}
                  onClick={() => {
                    setTestResult(null);
                    setTestOpen(true);
                  }}
                >
                  Test against customer…
                </Button>
                <Button
                  color="error"
                  disabled={busy}
                  onClick={() =>
                    void deleteIntegrationTemplate(selected.integrationKey).then(() => {
                      setSelected(null);
                      return load();
                    })
                  }
                >
                  Delete
                </Button>
              </Box>
            </SectionCard>
          )}
        </Box>
      )}

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Dry-run scan — {selected?.integrationKey}</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            label="Customer id / alias"
            value={testCustomer}
            onChange={(e) => setTestCustomer(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          {testResult && (
            <Box sx={{ fontSize: 12.5, fontFamily: brainTokens.font.mono, whiteSpace: "pre-wrap", bgcolor: c.panelBg, p: 2, borderRadius: "8px", maxHeight: 360, overflow: "auto" }}>
              {JSON.stringify(
                testResult.customers[0]?.detections.find((d) => d.integrationKey === selected?.integrationKey) ||
                  testResult.customers[0]?.detections ||
                  testResult,
                null,
                2,
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Close</Button>
          <Button variant="contained" disabled={busy || !testCustomer.trim()} onClick={() => void runDryTest()}>
            Run dry-run
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
